// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect } from 'react';
import clsx from 'clsx';

import { Portal } from '@amzn/awsui-component-toolkit/internal';

import { getBaseProps } from '../internal/base-component';
import { Transition } from '../internal/components/transition';
import { fireNonCancelableEvent } from '../internal/events';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import PopoverArrow from '../popover/arrow';
import PopoverBody from '../popover/body';
import PopoverContainer from '../popover/container';
import { InternalTooltipProps } from './internal-interfaces';

import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';

type InternalTooltipComponentProps = InternalTooltipProps &
  InternalBaseComponentProps & {
    // ID of the associated trigger. When the trigger and the tooltip are connected via referrer ID, the
    // nodeBelongs(triggerNode, tooltipNode) returns true.
    referrerId?: string;
  };

export default function InternalTooltip({
  content,
  getTrack,
  className,
  position = 'top',
  onEscape,
  referrerId,
  __internalRootRef,
  ...restProps
}: InternalTooltipComponentProps) {
  const baseProps = getBaseProps(restProps);
  const trackRef = React.useRef<HTMLElement | SVGElement | null>(null);

  // Update the ref with the current tracked element
  React.useEffect(() => {
    const element = getTrack();
    trackRef.current = element;
  }, [getTrack]);

  useEffect(() => {
    const controller = new AbortController();
    window.addEventListener(
      'keydown',
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          // Prevent any surrounding modals or dialogs from acting on this Esc.
          event.stopPropagation();
          fireNonCancelableEvent(onEscape);
        }
      },
      {
        // The tooltip is often activated on mouseover, which means the focus can
        // be anywhere else on the page. Capture also means that this gets called
        // before any wrapper modals or dialogs can detect it and act on it.
        capture: true,
        signal: controller.signal,
      }
    );
    return () => {
      controller.abort();
    };
  }, [onEscape]);

  return (
    <Portal>
      <div
        {...baseProps}
        className={clsx(testUtilStyles.root, styles.root, baseProps.className)}
        ref={__internalRootRef}
        data-awsui-referrer-id={referrerId}
        role="tooltip"
      >
        <Transition in={true}>
          {() => (
            <PopoverContainer
              getTrack={getTrack}
              size="medium"
              fixedWidth={false}
              position={position}
              zIndex={7000}
              arrow={position => <PopoverArrow position={position} />}
              hideOnOverscroll={true}
              className={className}
            >
              <PopoverBody dismissButton={false} dismissAriaLabel={undefined} onDismiss={undefined} header={undefined}>
                {content}
              </PopoverBody>
            </PopoverContainer>
          )}
        </Transition>
      </div>
    </Portal>
  );
}



// WEBPACK FOOTER //
// /local/p4clients/pkgbuild-const/src/tooltip/internal.tsx